import { useState, useEffect } from 'react';
import { Copy, Check, AlertCircle, ChevronDown, Send, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const DepositTab = ({ balance, onSuccess }) => {
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [selectedNetwork, setSelectedNetwork] = useState('Bitcoin');
  const [copied, setCopied] = useState(false);
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false);
  
  // Submission form states
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [transactionHash, setTransactionHash] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Primary: Fetch addresses from API (most reliable)
  const [apiAddresses, setApiAddresses] = useState(null);
  const [hasOverride, setHasOverride] = useState(false);
  const [addressesLoading, setAddressesLoading] = useState(true);

  // Fetch addresses on component mount.
  // /deposit-addresses/all returns BOTH user-specific override and platform default.
  // We merge them with user-specific taking precedence per (crypto, network) pair so the
  // admin's per-user override appears here; falls back to platform address otherwise.
  useEffect(() => {
    const fetchAddresses = async () => {
      setAddressesLoading(true);
      try {
        const response = await api.get('/deposit-addresses/all');
        const userAddrs = response.data?.user_addresses || {};
        const platformAddrs = response.data?.platform_addresses || {};

        // Deep-merge: start with platform, then overlay any user-specific override
        const merged = JSON.parse(JSON.stringify(platformAddrs || {}));
        let overrideFound = false;
        for (const crypto of Object.keys(userAddrs || {})) {
          if (typeof userAddrs[crypto] !== 'object' || !userAddrs[crypto]) continue;
          if (!merged[crypto]) merged[crypto] = {};
          for (const network of Object.keys(userAddrs[crypto])) {
            const addr = userAddrs[crypto][network];
            if (addr && String(addr).trim()) {
              merged[crypto][network] = addr;
              overrideFound = true;
            }
          }
        }

        if (Object.keys(merged).length > 0) {
          setApiAddresses(merged);
          setHasOverride(overrideFound);
        } else {
          console.warn('Deposit addresses: API returned empty data');
        }
      } catch (error) {
        console.error('Failed to fetch deposit addresses:', error);
        toast.error(`Failed to load deposit addresses: ${error.message}`);
      } finally {
        setAddressesLoading(false);
      }
    };

    fetchAddresses();

    // Live refresh on WebSocket push from admin
    const handleAddressChange = () => fetchAddresses();
    window.addEventListener('phantomtrades:deposit-addresses-changed', handleAddressChange);
    return () => {
      window.removeEventListener('phantomtrades:deposit-addresses-changed', handleAddressChange);
    };
  }, []);
  
  // Use API addresses as the primary source
  const finalAddresses = apiAddresses;

  const cryptoList = [
    { symbol: 'BTC', name: 'Bitcoin' },
    { symbol: 'ETH', name: 'Ethereum' },
    { symbol: 'USDT', name: 'Tether' },
    { symbol: 'BNB', name: 'Binance Coin' },
    { symbol: 'SOL', name: 'Solana' },
    { symbol: 'ADA', name: 'Cardano' },
    { symbol: 'XRP', name: 'Ripple' },
    { symbol: 'DOT', name: 'Polkadot' },
  ];

  // Get available networks and current address from real-time data
  const getAvailableNetworks = () => {
    if (!finalAddresses) {
      console.log('⏳ Addresses still loading...');
      return [];
    }
    
    if (!finalAddresses[selectedCrypto]) {
      console.log('⚠️ No addresses configured for:', selectedCrypto);
      console.log('📋 Available cryptos:', Object.keys(finalAddresses));
      return [];
    }
    
    const networks = finalAddresses[selectedCrypto];
    const networkList = Object.keys(networks).map(network => ({
      id: network,
      name: network,
      address: networks[network]
    }));
    
    console.log('✅ Networks for', selectedCrypto, ':', networkList);
    return networkList;
  };

  const availableNetworks = getAvailableNetworks();
  const currentNetwork = availableNetworks.find(n => n.id === selectedNetwork) || availableNetworks[0];
  const currentAddress = currentNetwork?.address || '';
  
  // Debug logging  
  useEffect(() => {
    console.log('📊 Deposit Tab State:', {
      selectedCrypto,
      selectedNetwork,
      addressesLoading,
      apiAddresses: apiAddresses ? Object.keys(apiAddresses) : 'null',
      finalAddresses: finalAddresses ? Object.keys(finalAddresses) : 'null',
      availableNetworks: availableNetworks.length,
      currentAddress: currentAddress || 'EMPTY'
    });
  }, [selectedCrypto, selectedNetwork, currentAddress, addressesLoading, apiAddresses, finalAddresses, availableNetworks]);

  // Update selected network when crypto changes
  useEffect(() => {
    if (availableNetworks.length > 0) {
      setSelectedNetwork(availableNetworks[0].id);
    }
  }, [selectedCrypto, apiAddresses, availableNetworks]);

  const handleCryptoChange = (symbol) => {
    setSelectedCrypto(symbol);
    // Network will be automatically updated by the useEffect when crypto changes
  };

  const handleCopy = async () => {
    if (!currentAddress) {
      toast.error('No address available to copy');
      return;
    }

    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(currentAddress);
      setCopied(true);
      toast.success('Address copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that block clipboard API
      try {
        // Create temporary textarea
        const textarea = document.createElement('textarea');
        textarea.value = currentAddress;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        
        setCopied(true);
        toast.success('Address copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Copy failed:', fallbackErr);
        toast.error('Unable to copy. Please copy manually.');
      }
    }
  };

  const handleSubmitDeposit = async (e) => {
    e.preventDefault();

    // Validation
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (!transactionHash || transactionHash.length < 10) {
      toast.error('Please enter a valid transaction hash');
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post('/deposits/submit', {
        amount: parseFloat(amount),
        crypto_type: selectedCrypto,
        network: currentNetwork.name,
        transaction_hash: transactionHash,
        notes: notes
      });

      toast.success('Deposit request submitted! Awaiting admin approval.');
      setAmount('');
      setTransactionHash('');
      setNotes('');
      setShowSubmissionForm(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Deposit submission error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to submit deposit request';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 py-2">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-2xl font-bold">Deposit Cryptocurrency</h2>
          <div className="flex items-center space-x-2">
            {!addressesLoading && (
              <>
                <div className="h-2 w-2 rounded-full bg-green-400 animate-pulse"></div>
                <span className="text-xs text-green-400">Live Addresses</span>
              </>
            )}
          </div>
        </div>
        <p className="text-gray-400">
          Send funds to the deposit address and submit confirmation
        </p>
        {hasOverride && (
          <div
            data-testid="custom-address-badge"
            className="mt-3 inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-purple-500/15 border border-purple-500/40"
          >
            <div className="h-1.5 w-1.5 rounded-full bg-purple-400"></div>
            <span className="text-xs font-semibold text-purple-300">
              Showing your personal deposit address (set by admin)
            </span>
          </div>
        )}
      </div>

      {/* Cryptocurrency Selection */}
      <div>
        <label className="text-sm font-bold text-gray-300 mb-3 block">Select Cryptocurrency</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {cryptoList.map((crypto) => (
            <button
              key={crypto.symbol}
              onClick={() => handleCryptoChange(crypto.symbol)}
              className={`p-4 rounded-xl font-bold transition-all border-2 ${
                selectedCrypto === crypto.symbol
                  ? 'bg-purple-600/30 border-purple-500 text-white shadow-lg shadow-purple-500/50'
                  : 'bg-gray-900/50 border-gray-700 text-gray-400 hover:border-gray-600'
              }`}
            >
              <div className="text-lg mb-1">{crypto.symbol}</div>
              <div className="text-xs text-gray-400">{crypto.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Network Selection */}
      {availableNetworks.length > 1 && (
        <div>
          <label className="text-sm font-bold text-gray-300 mb-3 block">Select Network</label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowNetworkDropdown(!showNetworkDropdown)}
              className="w-full px-4 py-3 bg-gray-900/50 text-white rounded-lg border-2 border-gray-700 hover:border-purple-500 transition-all flex items-center justify-between"
            >
              <span className="font-semibold">{currentNetwork?.name}</span>
              <ChevronDown className={`h-5 w-5 transition-transform ${showNetworkDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showNetworkDropdown && (
              <div className="absolute z-10 w-full mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl">
                {availableNetworks.map((network) => (
                  <button
                    key={network.id}
                    type="button"
                    onClick={() => {
                      setSelectedNetwork(network.id);
                      setShowNetworkDropdown(false);
                    }}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                      selectedNetwork === network.id ? 'bg-purple-600/20 text-purple-400' : 'text-white'
                    }`}
                  >
                    {network.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Network Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-blue-400 mb-1">Network: {currentNetwork?.name}</p>
            <p className="text-sm text-gray-300">
              Make sure to send only {selectedCrypto} to this address on the {currentNetwork?.name}. Sending other cryptocurrencies or using wrong network may result in loss of funds.
            </p>
          </div>
        </div>
      </div>

      {/* Deposit Address */}
      <div className="glow-card p-6 rounded-xl bg-gray-900/50">
        <h3 className="font-bold mb-4">Deposit Address</h3>

        {(addressesLoading && !apiAddresses) ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
            <span className="ml-3 text-gray-400">Loading address...</span>
          </div>
        ) : !currentAddress ? (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-yellow-400 mb-1">Address Not Available</p>
                <p className="text-sm text-gray-300">
                  Deposit address for {selectedCrypto} on {currentNetwork?.name || 'this network'} has not been configured yet. Please contact support or try another network.
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* Address Text */
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Address</label>
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-gray-800 p-4 rounded-lg">
                <p className="font-mono text-sm break-all text-white">{currentAddress}</p>
              </div>
              <button
                onClick={handleCopy}
                className="p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                title="Copy address"
              >
                {copied ? (
                  <Check className="h-5 w-5 text-white" />
                ) : (
                  <Copy className="h-5 w-5 text-white" />
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Submit Deposit Button */}
      {!showSubmissionForm && (
        <button
          onClick={() => setShowSubmissionForm(true)}
          className="w-full py-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-xl text-white font-bold flex items-center justify-center space-x-2 transition-all"
        >
          <Send className="h-5 w-5" />
          <span>I've Sent The Funds - Submit Deposit</span>
        </button>
      )}

      {/* Deposit Submission Form */}
      {showSubmissionForm && (
        <div className="glow-card p-6 rounded-xl bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30">
          <h3 className="font-bold mb-4 text-xl">Submit Deposit Confirmation</h3>
          
          <form onSubmit={handleSubmitDeposit} className="space-y-4">
            {/* Amount in Crypto */}
            <div>
              <label className="text-sm font-bold text-gray-300 mb-2 block">Amount ({selectedCrypto})</label>
              <div className="relative">
                <input
                  type="number"
                  step="any"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Enter amount in ${selectedCrypto}`}
                  className="w-full px-4 py-3 bg-gray-900/50 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-gray-700 text-lg font-bold pr-20"
                  required
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">
                  {selectedCrypto}
                </span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Enter the exact amount of {selectedCrypto} you sent
              </p>
            </div>

            {/* Transaction Hash */}
            <div>
              <label className="text-sm font-bold text-gray-300 mb-2 block">Transaction Hash / TXID</label>
              <input
                type="text"
                value={transactionHash}
                onChange={(e) => setTransactionHash(e.target.value)}
                placeholder="0x..."
                className="w-full px-4 py-3 bg-gray-900/50 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-gray-700 font-mono"
                required
              />
              <p className="text-xs text-gray-400 mt-1">
                Enter the transaction hash from your wallet
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="text-sm font-bold text-gray-300 mb-2 block">Notes (Optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional information..."
                className="w-full px-4 py-3 bg-gray-900/50 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-gray-700 h-20"
              />
            </div>

            {/* Info Banner */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-sm text-gray-300">
                Your deposit will be reviewed and credited to your account within 1-24 hours after confirmation.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex items-center space-x-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-5 w-5" />
                <span>{isSubmitting ? 'Submitting...' : 'Submit Deposit'}</span>
              </button>
              <button
                type="button"
                onClick={() => setShowSubmissionForm(false)}
                className="px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-900/50 rounded-xl p-6">
        <h3 className="font-bold mb-4">How to Deposit</h3>
        <div className="space-y-3">
          <div className="flex items-start space-x-3">
            <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold text-sm">
              1
            </div>
            <p className="text-gray-300 text-sm">
              Copy the deposit address above
            </p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold text-sm">
              2
            </div>
            <p className="text-gray-300 text-sm">
              Go to your external wallet or exchange and send {selectedCrypto}
            </p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold text-sm">
              3
            </div>
            <p className="text-gray-300 text-sm">
              Click "I've Sent The Funds" and submit the transaction hash for verification
            </p>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-bold text-sm">
              4
            </div>
            <p className="text-gray-300 text-sm">
              Wait for admin approval and your balance will be credited
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepositTab;
